import { createRoot } from 'react-dom/client';

import { Globals } from '@react-spring/web';

import * as perf from '@/mastodon/utils/performance';
import { setupBrowserNotifications } from 'mastodon/actions/notifications';
import Mastodon from 'mastodon/containers/mastodon';
import {
  me,
  reduceMotion,
  personalAccent,
  personalPurpleHue,
  personalFontDisplay,
  personalFontBody,
  uiScale,
} from 'mastodon/initial_state';
import ready from 'mastodon/ready';
import { store } from 'mastodon/store';

import { isProduction, isDevelopment } from './utils/environment';
import { applyPersonalAppearance } from './utils/personal_appearance';
import { applyThemeVariant } from './utils/theme_variant';

function main() {
  perf.start('main()');

  return ready(async () => {
    const mountNode = document.getElementById('mastodon');
    if (!mountNode) {
      throw new Error('Mount node not found');
    }
    const props = JSON.parse(
      mountNode.getAttribute('data-props') ?? '{}',
    ) as Record<string, unknown>;

    if (reduceMotion) {
      Globals.assign({
        skipAnimation: true,
      });
    }

    // Kronk theme variant — stamp data-theme on <html> so the light design
    // tokens activate under the light skin. Must run before applyPersonalAppearance
    // (which reads data-theme to pick its per-theme anchors).
    applyThemeVariant();

    // Kronk Personal Appearance — apply the user's per-user token overrides.
    applyPersonalAppearance({
      accent: personalAccent,
      purpleHue: personalPurpleHue,
      fontDisplay: personalFontDisplay,
      fontBody: personalFontBody,
      uiScale,
    });

    const { initializeEmoji } = await import('./features/emoji/index');
    initializeEmoji();

    const root = createRoot(mountNode);
    root.render(<Mastodon {...props} />);
    store.dispatch(setupBrowserNotifications());

    // Pick up a deploy in this already-open tab (utils/build_watcher).
    if (isProduction()) {
      const { startBuildWatcher } = await import('./utils/build_watcher');
      startBuildWatcher(() => store.getState());
    }

    if (isProduction() && me && 'serviceWorker' in navigator) {
      const { Workbox } = await import('workbox-window');
      const wb = new Workbox(
        isDevelopment() ? '/packs-dev/dev-sw.js?dev-sw' : '/sw.js',
        { type: 'module', scope: '/' },
      );
      let registration;

      try {
        registration = await wb.register();
      } catch (err) {
        console.error(err);
      }

      if (
        registration &&
        'Notification' in window &&
        Notification.permission === 'granted'
      ) {
        const registerPushNotifications = await import(
          'mastodon/actions/push_notifications'
        );

        store.dispatch(registerPushNotifications.register());
      }
    }

    perf.stop('main()');
  });
}

// eslint-disable-next-line import/no-default-export
export default main;
